package diana.soleil.design.iraniancanadienbazzar.feature.profile

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.background
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.BusinessCover
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.BusinessListItem
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EventCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.NewsCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.SectionHeader
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.categoryImageRes
import diana.soleil.design.iraniancanadienbazzar.core.model.categoryIconKey
import diana.soleil.design.iraniancanadienbazzar.core.model.cityLabel
import diana.soleil.design.iraniancanadienbazzar.core.model.localizeDigits
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.appContentWidth
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.profile_badge_new
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.profile_unsave_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.saved_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.saved_empty_businesses
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.saved_empty_events
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.saved_empty_news
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.saved_event_am
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.saved_event_date_format
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.saved_event_free
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.saved_event_image_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.saved_event_open
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.saved_event_pm
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.saved_months
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.saved_section_past
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.saved_section_upcoming
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.saved_tab_businesses
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.saved_tab_count
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.saved_tab_events
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.saved_tab_news
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.saved_title
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringArrayResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import kotlin.time.Instant

/**
 * Everything you kept, in one place: saved businesses, events you said you're going to, and
 * bookmarked news.
 *
 * Built 2026-08-03 (owner: "if I want to like a news or if I say I'm going to an event I don't have
 * any place that would show those for me"). Before this, an RSVP wrote to `users/{uid}/rsvps` and
 * was never readable back anywhere, and news had no save action at all.
 *
 * **Why tabs and not one long page.** The three collections have genuinely different card shapes and
 * different lifetimes, and a stacked page makes the shortest list the hardest to reach. Segmented
 * chips carry a count each, so the screen answers "do I have anything in there?" before you tap —
 * which is the thing a stacked page does well and tabs usually lose. Saved marketplace listings and
 * Hidden Gems deliberately stay out: both already surface inside their own hubs, and duplicating them
 * here would create two places to unsave the same thing.
 */
@Composable
fun SavedScreen(
    onBack: () -> Unit,
    onOpenBusiness: (String) -> Unit,
    onOpenEvent: (String) -> Unit,
    onOpenNews: (String) -> Unit,
    modifier: Modifier = Modifier,
    viewModel: SavedViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val tab by viewModel.tab.collectAsStateWithLifecycle()
    val lang = AppTheme.language

    Column(
        modifier = modifier
            .background(AppTheme.colors.background)
            .appContentWidth(AppTheme.sizes.contentMaxWidth)
            .fillMaxHeight()
            .statusBarsPadding()
            .navigationBarsPadding(),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(AppTheme.spacing.lg),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.saved_back_a11y),
                onClick = onBack,
                tint = AppTheme.colors.onSurface,
            )
            Text(
                text = stringResource(Res.string.saved_title),
                style = AppTheme.typography.headlineLg,
                color = AppTheme.colors.onSurface,
            )
        }

        // Counts live on the chips so an empty collection is visible without opening it.
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = AppTheme.spacing.lg),
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        ) {
            SavedTabChip(SavedTab.Businesses, tab, state.businesses.size, Res.string.saved_tab_businesses, viewModel::selectTab)
            SavedTabChip(SavedTab.Events, tab, state.eventCount, Res.string.saved_tab_events, viewModel::selectTab)
            SavedTabChip(SavedTab.News, tab, state.news.size, Res.string.saved_tab_news, viewModel::selectTab)
        }

        if (state.loading) {
            LoadingState()
            return@Column
        }

        LazyColumn(
            modifier = Modifier.fillMaxWidth(),
            contentPadding = PaddingValues(AppTheme.spacing.lg),
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            when (tab) {
                SavedTab.Businesses -> if (state.businesses.isEmpty()) {
                    item { EmptyState(title = stringResource(Res.string.saved_tab_businesses), description = stringResource(Res.string.saved_empty_businesses)) }
                } else {
                    items(state.businesses.size, key = { state.businesses[it].id }) { index ->
                        val business = state.businesses[index]
                        val icon = categoryIconKey(business.categoryId)
                        val imageRes = categoryImageRes(business.categoryId)
                        val coverUrl = business.coverUrl?.takeIf { it.isNotBlank() }
                        BusinessListItem(
                            name = business.displayName(lang),
                            category = business.serviceLine(lang),
                            rating = business.rating.toFloat(),
                            newLabel = stringResource(Res.string.profile_badge_new).takeIf { business.reviewCount == 0 },
                            distance = AppTheme.language.cityLabel(business.address.city),
                            favorite = true,
                            onFavoriteClick = { viewModel.toggleFavorite(business.id) },
                            favoriteContentDescription = stringResource(Res.string.profile_unsave_a11y),
                            onClick = { onOpenBusiness(business.id) },
                            coverIcon = icon,
                            coverImageRes = imageRes,
                            thumbnail = coverUrl?.let { url -> { BusinessCover(imageUrl = url, iconName = icon, placeholderImageRes = imageRes) } },
                        )
                    }
                }

                SavedTab.Events -> if (state.eventCount == 0) {
                    item { EmptyState(title = stringResource(Res.string.saved_tab_events), description = stringResource(Res.string.saved_empty_events)) }
                } else {
                    // Upcoming first: the nearest commitment is the reason you opened this tab.
                    if (state.upcomingEvents.isNotEmpty()) {
                        item { SectionHeader(title = stringResource(Res.string.saved_section_upcoming)) }
                        savedEvents(state.upcomingEvents, lang, onOpenEvent)
                    }
                    if (state.pastEvents.isNotEmpty()) {
                        item { SectionHeader(title = stringResource(Res.string.saved_section_past)) }
                        savedEvents(state.pastEvents, lang, onOpenEvent)
                    }
                }

                SavedTab.News -> if (state.news.isEmpty()) {
                    item { EmptyState(title = stringResource(Res.string.saved_tab_news), description = stringResource(Res.string.saved_empty_news)) }
                } else {
                    items(state.news.size, key = { state.news[it].id }) { index ->
                        val article = state.news[index]
                        NewsCard(
                            title = article.title.resolve(lang),
                            teaser = article.summary.resolve(lang).takeIf { it.isNotBlank() },
                            imageUrl = article.imageUrl,
                            onClick = { onOpenNews(article.id) },
                            // The rail sizes NewsCard to a fixed carousel width; here it is a full-width
                            // list row, so the caller overrides it.
                            modifier = Modifier.fillMaxWidth(),
                        )
                    }
                }
            }
        }
    }
}

/** One segmented chip with its count, e.g. "Events · 2". */
@Composable
private fun SavedTabChip(
    value: SavedTab,
    selected: SavedTab,
    count: Int,
    labelRes: org.jetbrains.compose.resources.StringResource,
    onSelect: (SavedTab) -> Unit,
) {
    AppChip(
        label = stringResource(Res.string.saved_tab_count, stringResource(labelRes), count),
        selected = value == selected,
        onClick = { onSelect(value) },
    )
}

private fun androidx.compose.foundation.lazy.LazyListScope.savedEvents(
    events: List<diana.soleil.design.iraniancanadienbazzar.core.model.Event>,
    lang: diana.soleil.design.iraniancanadienbazzar.core.model.Language,
    onOpenEvent: (String) -> Unit,
) {
    items(events.size, key = { events[it].id }) { index ->
        val event = events[index]
        val title = event.title.resolve(lang)
        EventCard(
            title = title,
            dateTime = savedEventDateTime(event.startsAtMillis),
            // Seeded events sometimes carry an empty venue name or city; joining the non-blank parts
            // avoids rendering a bare ", " next to the pin icon.
            venue = listOf(event.venue.name, AppTheme.language.cityLabel(event.venue.city)).filter { it.isNotBlank() }.joinToString(", "),
            actionLabel = stringResource(Res.string.saved_event_open),
            imageContentDescription = stringResource(Res.string.saved_event_image_a11y, title),
            onAction = { onOpenEvent(event.id) },
            onClick = { onOpenEvent(event.id) },
            coverUrl = event.coverUrl,
            badgeLabel = stringResource(Res.string.saved_event_free).takeIf { event.isFree },
            actionTrailingIcon = "chevron_right",
        )
    }
}

/**
 * "AUG 7 • 8:30 PM".
 *
 * Duplicated from `:feature:home` / `:feature:events` on purpose: a feature module may not depend on
 * another feature module (golden rule 5), and the localized month/AM-PM copy cannot live in
 * `:core:designsystem` because DS components hold no copy (golden rule 6). This is now the THIRD
 * copy — docs/PENDING.md tracks a shared i18n-capable module as the real fix.
 */
@Composable
private fun savedEventDateTime(epochMillis: Long): String {
    val value = Instant.fromEpochMilliseconds(epochMillis).toLocalDateTime(TimeZone.currentSystemDefault())
    val hour12 = when {
        value.hour == 0 -> 12
        value.hour > 12 -> value.hour - 12
        else -> value.hour
    }
    val months = stringArrayResource(Res.array.saved_months)
    // Persian digits in Farsi: resource args always render Western digits (see NumberFormatting).
    return AppTheme.language.localizeDigits(
        stringResource(
            Res.string.saved_event_date_format,
            months.getOrNull(value.month.ordinal) ?: "",
            value.day,
            hour12,
            value.minute.toString().padStart(2, '0'),
            stringResource(if (value.hour < 12) Res.string.saved_event_am else Res.string.saved_event_pm),
        ),
    )
}
